import { useCallback, useEffect, useMemo } from 'react';

import {
  ApiGrantRoleDto,
  ApiRoleResponse,
  ApiUpdateChatPermissionsDto,
} from '@/shared/api/generated/data-contracts';
import { useApi } from '@/shared/api/hooks/useApi';
import { useAuthStore } from '@/shared/store/auth.store';

import { useMembersStore } from '../stores/members.store';
import { usePermissionsStore } from '../stores/permissions.store';

export const usePermissions = () => {
  const api = useApi();
  const { account } = useAuthStore();
  const { isInitialized: isMembersInitialized, updateMember } = useMembersStore();

  const {
    userRoles,
    userPermissions,
    allRoles,
    allPermissions,
    chatPermissions,
    isLoading,
    setUserRoles,
    setUserPermissions,
    setAllRoles,
    setAllPermissions,
    setChatPermissions,
    removeUserRole,
    setLoading,
    setInitialized,
  } = usePermissionsStore();

  const fetchUserPermissions = useCallback(async () => {
    if (!account?.id || isLoading) return;

    setLoading(true);

    const [rolesResult, permissionsResult] = await Promise.allSettled([
      api.permissionsGetUserRoles(account.id),
      api.permissionsGetUserPermissions(account.id),
    ]);

    const roles = rolesResult.status === 'fulfilled' ? rolesResult.value : null;
    const permissions = permissionsResult.status === 'fulfilled' ? permissionsResult.value : null;

    if (roles?.data) {
      setUserRoles(roles.data);
    }

    if (permissions?.data) {
      setUserPermissions(permissions.data);
    }

    setLoading(false);
  }, [account?.id, isLoading, api, setUserRoles, setUserPermissions, setLoading]);

  const fetchAllRoles = useCallback(async () => {
    if (isLoading) return;

    setLoading(true);

    const response = await api.permissionsGetAllRoles();

    if (response.data) {
      setAllRoles(response.data);
    }

    setLoading(false);
  }, [api, isLoading, setLoading, setAllRoles]);

  const fetchAllPermissions = useCallback(async () => {
    if (isLoading) return;

    setLoading(true);

    const response = await api.permissionsGetAll();

    if (response.data) {
      setAllPermissions(response.data);
    }

    setLoading(false);
  }, [api, isLoading, setLoading, setAllPermissions]);

  const fetchChatPermissions = useCallback(async () => {
    if (!account?.id || isLoading) return null;

    setLoading(true);

    const response = await api.permissionsGetChat(account.id);

    setLoading(false);

    if (response.data) {
      setChatPermissions(response.data);
      return response.data;
    }

    return null;
  }, [account?.id, isLoading, api, setChatPermissions, setLoading]);

  const fetchMemberChatPermissions = useCallback(
    async (profileId: number) => {
      const response = await api.permissionsGetChat(profileId);

      if (response.data) {
        return response.data;
      }

      return null;
    },
    [api],
  );

  const getUserRoles = useCallback(
    async (profileId: number) => {
      const res = await api.permissionsGetUserRoles(profileId);

      if (res.data) {
        return res.data;
      }

      return null;
    },
    [api],
  );

  const grantRole = useCallback(
    async (profileId: number, roleCode: ApiGrantRoleDto['roleCode']) => {
      setLoading(true);
      const res = await api.permissionsGrantRole(profileId, { roleCode });
      setLoading(false);

      if (res.error) {
        return false;
      }

      if (res.data) {
        updateMember(profileId, { accessLevel: res.data.roleCode });
      }

      return true;
    },
    [api, setLoading, updateMember, account?.id],
  );

  const revokeRole = useCallback(
    async (profileId: number, roleId: string) => {
      setLoading(true);

      const response = await api.permissionsRevokeRole(profileId, roleId);

      setLoading(false);

      if (response.error) {
        return false;
      }

      if (profileId === account?.id) {
        removeUserRole(roleId);
      }

      return true;
    },
    [api, setLoading, account?.id, removeUserRole],
  );

  const updateChatPermissions = useCallback(
    async (profileId: number, dto: ApiUpdateChatPermissionsDto) => {
      setLoading(true);

      const response = await api.permissionsUpdateChat(profileId, dto);

      setLoading(false);

      if (response.error) {
        return false;
      }

      if (response.data && profileId === account?.id) {
        setChatPermissions(response.data);
      }

      return true;
    },
    [api, setLoading, account?.id, setChatPermissions],
  );

  const hasAllRoles = useCallback(
    (roleCodes: string[]): boolean => {
      return roleCodes.every((code) => userRoles.some((ur) => ur.roleCode === code));
    },
    [userRoles],
  );

  const hasAnyRole = useCallback(
    (roleCodes: string[]): boolean => {
      return roleCodes.some((code) => userRoles.some((ur) => ur.roleCode === code));
    },
    [userRoles],
  );

  const hasAllPermissions = useCallback(
    (permissionCodes: string[]): boolean => {
      return permissionCodes.every((code) =>
        userPermissions.some((up) => up.permissionCode === code && up.isGranted),
      );
    },
    [userPermissions],
  );

  const hasAnyPermission = useCallback(
    (permissionCodes: string[]): boolean => {
      return permissionCodes.some((code) =>
        userPermissions.some((up) => up.permissionCode === code && up.isGranted),
      );
    },
    [userPermissions],
  );

  const getRole = useCallback(
    (roleCode: string): ApiRoleResponse | undefined => {
      return allRoles.find((r) => r.code === roleCode);
    },
    [allRoles],
  );

  const getPermission = useCallback(
    (permissionCode: string) => {
      return allPermissions.find((p) => p.code === permissionCode);
    },
    [allPermissions],
  );

  const fetchAll = async () => {
    const res = await Promise.allSettled([
      fetchUserPermissions(),
      fetchAllRoles(),
      fetchAllPermissions(),
      fetchChatPermissions(),
    ]);
    if (res.every((item) => item.status === 'fulfilled')) {
      setInitialized(true);
    }
  };

  useEffect(() => {
    const { isInitialized, isLoading: isLoadingLocal } = usePermissionsStore.getState();
    if (isInitialized || isLoadingLocal || !isMembersInitialized || !account?.id) return;
    fetchAll();
  }, [isMembersInitialized, account?.id]);

  return useMemo(
    () => ({
      userRoles,
      userPermissions,
      allRoles,
      allPermissions,
      chatPermissions,
      isLoading,
      fetchUserPermissions,
      fetchAllRoles,
      fetchAllPermissions,
      fetchChatPermissions,
      getUserRoles,
      grantRole,
      revokeRole,
      updateChatPermissions,
      hasAllRoles,
      hasAllPermissions,
      getRole,
      getPermission,
      hasAnyRole,
      hasAnyPermission,
      fetchMemberChatPermissions,
    }),
    [
      userRoles,
      userPermissions,
      allRoles,
      allPermissions,
      chatPermissions,
      isLoading,
      fetchUserPermissions,
      fetchAllRoles,
      fetchAllPermissions,
      fetchChatPermissions,
      getUserRoles,
      grantRole,
      revokeRole,
      updateChatPermissions,
      hasAllRoles,
      hasAllPermissions,
      getRole,
      getPermission,
      hasAnyRole,
      hasAnyPermission,
      fetchMemberChatPermissions,
    ],
  );
};
